/**
 * Run vocabulary as types. One place maps each status to its pill class, glyph class, timeline class, toast class
 * and label. A status is always colour, glyph and word, so every consumer takes all three from here.
 */

export const RUN_STATUSES = [
  "claimed",
  "overlapping",
  "negotiating",
  "verified",
  "failed",
  "landed",
  "promoted",
  "neutral",
] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];

export interface StatusInfo {
  readonly status: RunStatus;
  /** The word shown beside the glyph. Sentence case. */
  readonly label: string;
  readonly pillClass: string;
  readonly glyphClass: string;
  readonly timelineClass: string;
  readonly toastClass: string;
  readonly mapClass: string;
  /** CSS variable names, for canvas or chart code that cannot use classes. */
  readonly colourVar: string;
  readonly softVar: string;
  /** Verdict-style failures read assertively; everything else politely. */
  readonly live: "polite" | "assertive";
}

function info(status: RunStatus, label: string): StatusInfo {
  return {
    status,
    label,
    pillClass: `bt-pill--${status}`,
    glyphClass: `bt-glyph--${status}`,
    timelineClass: `bt-tl-item--${status}`,
    toastClass: `bt-toast--${status}`,
    mapClass: `dir--${status}`,
    colourVar: `--status-${status}`,
    softVar: `--status-${status}-soft`,
    live: status === "failed" ? "assertive" : "polite",
  };
}

export const STATUS: Readonly<Record<RunStatus, StatusInfo>> = {
  claimed: info("claimed", "Claimed"),
  overlapping: info("overlapping", "Overlapping"),
  negotiating: info("negotiating", "Negotiating"),
  verified: info("verified", "Verified"),
  failed: info("failed", "Failed"),
  landed: info("landed", "Landed"),
  promoted: info("promoted", "Promoted"),
  neutral: info("neutral", "Idle"),
};

export function isRunStatus(value: unknown): value is RunStatus {
  return typeof value === "string" && (RUN_STATUSES as readonly string[]).includes(value);
}

/** Statuses that are a quick confirmation. Their toasts clear themselves; the rest wait for the human. */
export const CONFIRMATION_STATUSES: readonly RunStatus[] = ["claimed", "verified", "landed", "promoted", "neutral"];
